export type GroupedPermissions = Record<string, string[]>;

function toActionList(value: unknown): string[] {
  if (value == null || typeof value === "function") return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (
          item &&
          typeof item === "object" &&
          "name" in item &&
          typeof (item as {name: unknown}).name === "string"
        ) {
          return (item as {name: string}).name.trim();
        }
        return null;
      })
      .filter((s): s is string => Boolean(s));
  }
  if (typeof value === "object") {
    return Object.keys(value as Record<string, unknown>);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
}

/** Coerce API / legacy shapes into `{ resource: action[] }`. */
export function normalizeGroupedPermissions(input: unknown): GroupedPermissions {
  if (input == null) return {};

  if (Array.isArray(input)) {
    const result: GroupedPermissions = {};
    for (const item of input) {
      const name =
        typeof item === "string"
          ? item.trim()
          : item &&
              typeof item === "object" &&
              "name" in item &&
              typeof (item as {name: unknown}).name === "string"
            ? (item as {name: string}).name.trim()
            : null;
      if (!name) continue;
      const parsed = parsePermissionName(name);
      if (!parsed) continue;
      if (!result[parsed.resource]) result[parsed.resource] = [];
      if (!result[parsed.resource].includes(parsed.action)) {
        result[parsed.resource].push(parsed.action);
      }
    }
    return result;
  }

  if (typeof input !== "object") {
    return {};
  }

  const result: GroupedPermissions = {};
  for (const [resource, actions] of Object.entries(input as Record<string, unknown>)) {
    const list = toActionList(actions);
    if (list.length > 0) {
      result[resource] = list;
    }
  }
  return result;
}

/** Split `"resource action"` on the last space (resources may contain hyphens). */
export function parsePermissionName(name: string): {resource: string; action: string} | null {
  const trimmed = name.trim();
  const spaceIndex = trimmed.lastIndexOf(" ");
  if (spaceIndex <= 0) return null;
  return {
    resource: trimmed.slice(0, spaceIndex),
    action: trimmed.slice(spaceIndex + 1),
  };
}

export function flattenGroupedPermissions(grouped?: GroupedPermissions | null): Set<string> {
  const names = new Set<string>();
  const normalized = normalizeGroupedPermissions(grouped);
  for (const [resource, actions] of Object.entries(normalized)) {
    for (const action of actions) {
      names.add(`${resource} ${action}`);
    }
  }
  return names;
}

export function groupedToFlatList(grouped: GroupedPermissions | unknown): string[] {
  return [...flattenGroupedPermissions(grouped as GroupedPermissions)];
}

export function hasGroupedPermission(
  grouped: GroupedPermissions | undefined,
  permission: string,
): boolean {
  const trimmed = permission.trim();
  if (!trimmed) return false;

  // Grouped API may use a short resource key with multi-word actions
  // (e.g. storedvehicle + "show" → "storedvehicle show").
  if (flattenGroupedPermissions(grouped).has(trimmed)) return true;

  const parsed = parsePermissionName(trimmed);
  if (!parsed) return false;
  const actions = normalizeGroupedPermissions(grouped)[parsed.resource] ?? [];
  return actions.includes(parsed.action);
}

export function canAccessGrouped(
  grouped: GroupedPermissions | undefined,
  resource: string,
  action: string,
): boolean {
  const actions = normalizeGroupedPermissions(grouped)[resource] ?? [];
  return actions.includes(action);
}

/** Effective minus direct extras → permissions inherited from role only. */
export function subtractDirectFromEffective(
  effective: GroupedPermissions | unknown,
  direct: GroupedPermissions | unknown,
): GroupedPermissions {
  const effectiveGrouped = normalizeGroupedPermissions(effective);
  const directGrouped = normalizeGroupedPermissions(direct);
  const result: GroupedPermissions = {};
  for (const [resource, effectiveActions] of Object.entries(effectiveGrouped)) {
    const directActions = new Set(directGrouped[resource] ?? []);
    const fromRole = effectiveActions.filter((action) => !directActions.has(action));
    if (fromRole.length > 0) {
      result[resource] = fromRole;
    }
  }
  return result;
}

export function isPermissionInGrouped(grouped: GroupedPermissions | unknown, permission: string): boolean {
  return flattenGroupedPermissions(grouped as GroupedPermissions).has(permission);
}
